import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CourseCard from '@/components/base/CourseCard';
import { getAllCategories, getHotCourses, levelOptions, searchCourses } from '@/utils/course';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const cat = searchParams.get('cat') ?? '';
  const level = searchParams.get('level') ?? '';
  const [input, setInput] = useState(q);

  const categories = getAllCategories();

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
  };

  const baseResults = useMemo(() => searchCourses(q), [q]);

  const results = useMemo(() => {
    let list = baseResults;
    if (cat) list = list.filter((c) => c.category === cat);
    if (level) list = list.filter((c) => c.level === level);
    return list;
  }, [baseResults, cat, level]);

  const hotCourses = getHotCourses(4);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams({ q: input.trim() });
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
        <span>/</span>
        <span>搜索结果</span>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 mb-6">
        <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-4">
          课程搜索
        </h1>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center h-12 rounded-lg border border-background-300 bg-background-50 px-4">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400 text-lg">
              <i className="ri-search-line"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入课程关键词，如「英语」「Python」「理财」"
              className="flex-1 bg-transparent border-0 outline-none px-3 text-sm text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <div className="mt-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 w-14 shrink-0">课程类别</span>
            <button
              type="button"
              onClick={() => updateParams({ cat: '' })}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                !cat ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => updateParams({ cat: c.id })}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  cat === c.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 w-14 shrink-0">难度等级</span>
            <button
              type="button"
              onClick={() => updateParams({ level: '' })}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                !level ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {levelOptions.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => updateParams({ level: l })}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  level === l ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {q && (
        <p className="text-sm text-foreground-600 mb-5">
          关键词「<strong className="text-foreground-950">{q}</strong>」共找到{' '}
          <strong className="text-primary-600">{results.length}</strong> 个结果
        </p>
      )}

      {results.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {results.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      ) : (
        <div>
          <div className="flex flex-col items-center justify-center text-center py-16 rounded-lg border border-dashed border-background-300 bg-background-50 mb-10">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h3 className="font-heading font-bold text-lg text-foreground-900 mb-1">
              {q ? '未找到相关课程' : '输入关键词开始搜索'}
            </h3>
            <p className="text-sm text-foreground-500 mb-5">
              {q ? '换个关键词，或试试下方的热门课程吧。' : '试试搜索「英语」「数据分析」「理财」等关键词。'}
            </p>
            {q && (
              <button
                type="button"
                onClick={() => {
                  setInput('');
                  setSearchParams(new URLSearchParams());
                }}
                className="h-10 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重新搜索
              </button>
            )}
          </div>

          <section>
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-5">
              热门课程推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {hotCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}